/**
 * Event tracker: page-level (slow_page_load).
 * @package AdvancedTrackingManager
 */

import type { AtmConfig } from '@lib/types';
import { sendEvent } from './core';

const w = globalThis as unknown as Window;

/**
 * Get load duration in ms from Performance API (Navigation Timing).
 * Prefers PerformanceNavigationTiming; falls back to legacy performance.timing.
 */
function getLoadDurationMs(): number | null {
  const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  if (nav && typeof nav.loadEventEnd === 'number' && typeof nav.fetchStart === 'number') {
    const duration = nav.loadEventEnd - nav.fetchStart;
    return duration >= 0 ? Math.round(duration) : null;
  }
  const timing = (performance as Performance & { timing?: PerformanceTiming }).timing;
  if (timing?.loadEventEnd && timing.navigationStart) {
    const duration = timing.loadEventEnd - timing.navigationStart;
    return duration >= 0 ? duration : null;
  }
  return null;
}

/**
 * Check load duration and send slow_page_load if over threshold.
 * Call this after consent (and after load has completed).
 */
export function checkAndSendSlowPageLoad(
  config: AtmConfig,
  enable: { slowPageLoad: boolean }
): void {
  if (!enable.slowPageLoad) return;

  const thresholdMs = Math.max(500, Math.min(60000, config.slowPageLoadThresholdMs ?? 3000));
  const loadTimeMs = getLoadDurationMs();
  if (loadTimeMs === null || loadTimeMs < thresholdMs) return;

  sendEvent(
    'slow_page_load',
    {
      load_time_ms: loadTimeMs,
      threshold_ms: thresholdMs,
      page_title: document.title || '',
      page_location: w.location?.href ?? w.location?.pathname ?? '',
    },
    config
  );
}
